import { useQuery } from "@tanstack/react-query";
import { format, isValid } from "date-fns";

import { createAxiosInstance } from "api/axiosConfig";

const formatDateSafe = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return isValid(date) ? format(date, "dd-MM-yyyy") : "";
};

export const transformCDMDataFromAPI = (nestedData) => {
  const { clinicalData } = nestedData;
  const {
    diagnosis,
    emergencyAdmissions,
    medicationReview,
    riskFactors,
    physicalExam,
    diabetesSpecifics,
    asthmaCopdSpecifics,
    diagnosticInvestigations,
    laboratoryInvestigations,
    diseaseAssessmentScores,
    patientEducation,
    patientDemographics,
    otherMajorDiagnoses,
  } = clinicalData;

  return {
    ethnicityCode: patientDemographics?.ethnicityCode,
    status: nestedData.status ?? nestedData.clinicalMessageStatus,
    statusErrors: nestedData.statusErrors ?? [],
    statusWarnings: nestedData.statusWarnings ?? [],
    clinicalMessageStatus: nestedData.clinicalMessageStatus ?? null,
    clinicalMessageControlId: nestedData.clinicalMessageControlId ?? null,
    clinicalMessageXml: nestedData.clinicalMessageXml ?? null,
    clinicalAckCode: nestedData.clinicalAckCode ?? null,
    reimbursementMessageStatus: nestedData.reimbursementMessageStatus ?? null,
    reimbursementMessageControlId: nestedData.reimbursementMessageControlId ?? null,
    reimbursementAckCode: nestedData.reimbursementAckCode ?? null,

    // Deregistration reviews carry a compact clinicalData audit snapshot
    // (reasonCode / reasonDescription) instead of the full clinical dataset.
    reasonCode: clinicalData?.reasonCode ?? null,
    reasonDescription: clinicalData?.reasonDescription ?? null,
    registeredDate: new Date(nestedData.reviewDate),
    reviewingDate: new Date(nestedData.reviewDate),
    reviewingDoctor: nestedData.doctorName,
    doctorId: nestedData.doctorId ?? null,
    createdBy: nestedData.doctorName,
    reviewDate: nestedData.reviewDate ?? null,
    consultationDate:
      formatDateSafe(nestedData.reviewDate) || format(new Date(), "dd-MM-yyyy"),
    eircode: patientDemographics?.eircode ?? "",
    yearOfDiagnosis: diagnosis?.yearOfDiagnosis ?? {},
    attendingHospital: diagnosis?.attendingHospital ?? {},

    registrationReasons: diagnosis?.diagnoses,

    otherMajorDiagnoses: {
      diagnoses: otherMajorDiagnoses?.diagnoses ?? [],
      otherText: otherMajorDiagnoses?.otherText ?? "",
    },

    edAttendancesLast6Months: emergencyAdmissions?.edAttendancesLast6Months,
    unscheduledAdmissionsLast6Months:
      emergencyAdmissions?.unscheduledAdmissionsLast6Months,

    medicationReview: medicationReview ? "yes" : "no",

    smokingStatus: riskFactors?.smokingStatus,
    vapingStatus: riskFactors?.vapingStatus,
    weight: riskFactors?.weightKg?.toString(),
    height: riskFactors?.heightCm?.toString(),
    bmi: riskFactors?.bmi?.toString(),
    waistCircumference: riskFactors?.waistCircumferenceCm?.toString(),
    qRisk3Score: riskFactors?.qrisk3Score?.toString(),
    alcoholRiskScore: riskFactors?.alcoholAuditCScore?.toString(),
    alcoholFullAuditScore: riskFactors?.alcoholFullAuditScore?.toString(),
    physicalActivityQ1: riskFactors?.physicalActivityQ1,
    physicalActivityQ2: riskFactors?.physicalActivityQ2,
    smokingInterventions: riskFactors?.smokingInterventions ?? [],
    smokingInterventionOther: riskFactors?.smokingInterventionOther,
    alcoholDependenceInterventions:
      riskFactors?.alcoholDependenceInterventions ?? [],
    alcoholHigherRiskInterventions:
      riskFactors?.alcoholHigherRiskInterventions ?? [],
    alcoholIncreasingRiskInterventions:
      riskFactors?.alcoholIncreasingRiskInterventions ?? [],
    weightHighRiskInterventions: riskFactors?.weightHighRiskInterventions ?? [],
    weightIncreasedRiskInterventions:
      riskFactors?.weightIncreasedRiskInterventions ?? [],
    weightNormalInterventions: riskFactors?.weightNormalInterventions ?? [],
    physicalActivityInterventions:
      riskFactors?.physicalActivityInterventions ?? [],

    influenzaVaccine: riskFactors?.influenzaVaccine,
    influenzaVaccineDate: formatDateSafe(riskFactors?.influenzaVaccineDate),

    pneumococcalVaccine: riskFactors?.pneumococcalVaccine,
    pneumococcalVaccineDate: formatDateSafe(
      riskFactors?.pneumococcalVaccineDate
    ),
    covidVaccine: riskFactors?.covidVaccine,
    covidVaccineStatusUpToDate: riskFactors?.covidVaccineStatusUpToDate
      ? "YES"
      : "NO",

    pulse: physicalExam?.pulseRate?.toString(),
    pulseRhythm: physicalExam?.pulseRhythm,
    bp1: physicalExam?.systolicBloodPressure?.toString(),
    bp2: physicalExam?.diastolicBloodPressure?.toString(),

    diabetesRelatedAmputation: diabetesSpecifics?.diabetesRelatedAmputation
      ? "YES"
      : "NO",
    amputationYear: diabetesSpecifics?.amputationYear?.toString(),
    footAndLimbReviewFindings: diabetesSpecifics?.footAndLimbReviewFindings,
    footAndLimbReviewComment: diabetesSpecifics?.footAndLimbReviewComment,
    retinaScreeningUpToDate: diabetesSpecifics?.retinaScreeningUpToDate
      ? "YES"
      : "NO",
    referredForRetinaScreening: diabetesSpecifics?.referredForRetinaScreening,

    exacerbationsLast6Months: asthmaCopdSpecifics?.exacerbationsLast6Months,

    ecgSinceLastReview: diagnosticInvestigations?.ecgSinceLastReview
      ? "YES"
      : "NO",
    ecgResult: diagnosticInvestigations?.ecgResult,
    echoSinceLastReview:
      diagnosticInvestigations?.echoSinceLastReview === true
        ? "YES"
        : diagnosticInvestigations?.echoSinceLastReview === false
        ? "NO"
        : diagnosticInvestigations?.echoSinceLastReview === null
        ? "NOT_AVAILABLE"
        : "",
    echoResult: diagnosticInvestigations?.echoResult,
    spirometrySinceLastReview:
      diagnosticInvestigations?.spirometrySinceLastReview ? "YES" : "NO",
    spirometryResult: diagnosticInvestigations?.spirometryResult,

    haemoglobin: laboratoryInvestigations?.haemoglobin?.toString(),
    totalCholesterol: laboratoryInvestigations?.totalCholesterol?.toString(),
    hdlCholesterol: laboratoryInvestigations?.hdlCholesterol?.toString(),
    nonHdlCholesterol: laboratoryInvestigations?.nonHdlCholesterol?.toString(),
    ldlCholesterol: laboratoryInvestigations?.ldlCholesterol,
    triglycerides: laboratoryInvestigations?.triglycerides?.toString(),
    hba1c: laboratoryInvestigations?.hba1c?.toString(),
    serumCreatinine: laboratoryInvestigations?.serumCreatinine?.toString(),
    egfr: laboratoryInvestigations?.egfr,
    acr: laboratoryInvestigations?.acr,
    tftConducted: laboratoryInvestigations?.tftConducted ? "YES" : "NO",
    lftConducted: laboratoryInvestigations?.lftConducted ? "YES" : "NO",
    fastingGlucose: laboratoryInvestigations?.fastingGlucose,
    creatinineClearance:
      laboratoryInvestigations?.creatinineClearance?.toString(),
    ntProBNP: laboratoryInvestigations?.ntProBNP,

    copdDyspnoeaScore: diseaseAssessmentScores?.copdDyspnoeaScore?.toString(),
    cha2ds2vascScore: diseaseAssessmentScores?.cha2ds2vascScore?.toString(),
    // Opaque per-factor breakdowns round-tripped for cross-device carry-forward
    // (KIN-1261). undefined lets the seed logic fall back to localStorage.
    cha2ds2vascFactors: diseaseAssessmentScores?.cha2ds2vascFactors ?? undefined,
    qRisk3Conditions: riskFactors?.qRisk3Conditions ?? undefined,

    educationProvided: patientEducation?.educationProvided,
    referredToEducation: patientEducation?.referredToEducation,
    referredToDiabetesPrevention:
      patientEducation?.referredToDiabetesPrevention,
    referralDetails: patientEducation?.referralDetails,
    agreedWrittenCarePlan: patientEducation?.agreedWrittenCarePlan,

    carePlan: clinicalData?.carePlan,
  };
};

export const useGetCDM = (cdmId) => {

  const tenantVal = localStorage.getItem("kinesin-tenant");

  return useQuery({
    queryKey: ["cdmItem", cdmId, tenantVal],
    queryFn: async () => {
      if (!cdmId) return null;
      const axiosInstance = await createAxiosInstance();
      const response = await axiosInstance.get(
        `${tenantVal}/cdm/reviews/${cdmId}`
      );

      return transformCDMDataFromAPI(response.data);
    },
    enabled: !!cdmId,
    meta: { errorMessage: "Error fetching CDM" },
  });
};
